import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateDistance, getActivityIcon } from "@/lib/utils";
import { useLocation } from "wouter";
import type { ActivityLog as ActivityLogType } from "@shared/schema";

// Define interface that matches the API response format
interface ActivityLog extends Omit<ActivityLogType, 'timestamp'> {
  timestamp: string; // The API returns timestamp as string, not Date
}

function ActivityItem({ activity }: { activity: ActivityLog }) {
  const { icon, color } = getActivityIcon(activity.type);
  const timeAgo = formatDateDistance(activity.timestamp);
  
  return (
    <div className="flex items-start pb-4 border-b border-slate-100">
      <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center text-white flex-shrink-0`}>
        <i className={icon}></i>
      </div>
      <div className="ml-4 overflow-hidden">
        <p className="text-sm text-slate-800 break-words">{activity.description}</p>
        <p className="text-xs text-slate-500 mt-1">{timeAgo}</p>
      </div>
    </div>
  );
}

export default function RecentActivity() {
  const [, navigate] = useLocation();
  const { data, isLoading, error } = useQuery<ActivityLog[]>({
    queryKey: ['/api/activity-logs'],
    placeholderData: [],
  });

  const activities: ActivityLog[] = data || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between border-b border-slate-200">
        <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
        <span className="text-xs text-slate-500">Last 7 days</span>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          {isLoading ? (
            [...Array(4)].map((_, index) => (
              <div key={index} className="flex items-start pb-4 border-b border-slate-100">
                <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
                <div className="ml-4 flex-1 overflow-hidden">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-2/3 mt-1" />
                </div>
              </div>
            ))
          ) : error ? (
            <div className="text-error">Error loading activity logs</div>
          ) : (
            activities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))
          )}
        </div>
        {!isLoading && !error && (
          <div className="mt-4 text-center">
            <Button 
              variant="link" 
              className="text-sm text-primary font-medium hover:text-primary/80"
              onClick={() => navigate('/activity')}
            >
              View All Activity
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
